import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Plus, Search, Building2, MapPin, ExternalLink, MoreHorizontal,
  Eye, Edit, Trash2, Sun, Battery, Zap, Calendar as CalendarIcon, Sparkles, AlertTriangle, FileText, X, Mail, Upload, Loader2,
  SlidersHorizontal, RotateCcw, ChevronUp, ChevronDown, Phone
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '../components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SearchSelect from '../components/SearchSelect';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { propertyAPI, customerAPI, installationAPI, cleaningAPI, offerteAPI } from '../lib/api';
import { getGoogleMapsUrl, getInstallationTypeBadgeClass, getInstallationTypeLabel, formatDate, needsCleaning } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getSuggestions, findPostcodeByCity } from '../lib/belgianPostcodes';

// Filter Section Component
const FilterSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50 rounded-lg transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

const PropertyForm = ({ property, customers, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState(property || {
    customer_id: '',
    street: '',
    number: '',
    postal_code: '',
    city: '',
    country: 'Belgium',
    notes: ''
  });
  const [lookingUpPostcode, setLookingUpPostcode] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-lookup city when postcode is entered (4 digits for Belgian postcodes)
  const handlePostcodeChange = async (value) => {
    handleChange('postal_code', value);
    
    // Belgian postcodes are 4 digits
    if (value.length === 4 && /^\d{4}$/.test(value) && !formData.city) {
      setLookingUpPostcode(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lookup/postcode/${value}`);
        const data = await response.json();
        if (data.found && data.city) {
          handleChange('city', data.city);
        }
      } catch (error) {
        console.error('Postcode lookup failed:', error);
      } finally {
        setLookingUpPostcode(false);
      }
    }
  };

  // Auto-lookup postcode when city is entered (and postcode is empty)
  // Only triggers on blur OR when city is fully typed (4+ chars) to avoid premature matches
  const handleCityChange = (value) => {
    handleChange('city', value);
    // Show city suggestions dropdown
    setCitySuggestions(getSuggestions(value));
    setShowCitySuggestions(true);
  };
  
  // Lookup postcode on blur (when user finishes typing)
  const handleCityBlur = async (value) => {
    setTimeout(() => setShowCitySuggestions(false), 200);
    if (value.length >= 2 && !formData.postal_code) {
      // First try local lookup
      const postcode = findPostcodeByCity(value, false);
      if (postcode) {
        handleChange('postal_code', postcode);
        return;
      }
      // Fallback to backend API
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lookup/city/${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.found && data.postcode) {
          handleChange('postal_code', data.postcode);
        }
      } catch (error) {
        console.error('City lookup failed:', error);
      }
    }
  };
  
  // Handle city suggestion selection
  const handleCitySuggestionSelect = (suggestion) => {
    handleChange('city', suggestion.city);
    handleChange('postal_code', suggestion.postcode);
    setShowCitySuggestions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Prepare customer options for search select
  const customerOptions = customers.map(c => ({
    value: c.id,
    label: `${c.first_name} ${c.last_name}`
  }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="customer_id">Klant *</Label>
        <SearchSelect
          options={customerOptions}
          value={formData.customer_id}
          onChange={(value) => handleChange('customer_id', value)}
          placeholder="Zoek klant..."
          displayKey="label"
          valueKey="value"
          data-testid="property-customer-select"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="street">Straat *</Label>
          <AddressAutocomplete
            value={formData.street}
            onChange={(v) => setFormData(prev => ({ ...prev, street: v, street_fr: '' }))}
            onSelect={(s) => {
              setFormData(prev => ({
                ...prev,
                street: s.street || prev.street,
                number: s.number || prev.number,
                postal_code: s.postal_code || prev.postal_code,
                city: s.city || prev.city,
                street_fr: s.street_fr || '',
                city_fr: s.city_fr || '',
                country: (s.country && s.country !== 'België') ? s.country : prev.country,
              }));
            }}
            placeholder="Typ straatnaam..."
            required
            testId="property-street"
            cityHint={formData.city}
            postcodeHint={formData.postal_code}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="number">Nummer *</Label>
          <Input
            id="number"
            value={formData.number}
            onChange={(e) => handleChange('number', e.target.value)}
            required
            data-testid="property-number"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="postal_code">Postcode *</Label>
          <div className="relative">
            <Input
              id="postal_code"
              value={formData.postal_code}
              onChange={(e) => handlePostcodeChange(e.target.value)}
              required
              maxLength={4}
              placeholder="bv. 1000"
              data-testid="property-postal"
            />
            {lookingUpPostcode && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2 relative">
          <Label htmlFor="city">Gemeente *</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => handleCityChange(e.target.value)}
            onFocus={() => {
              if (formData.city.length >= 2) {
                setCitySuggestions(getSuggestions(formData.city));
                setShowCitySuggestions(true);
              }
            }}
            onBlur={(e) => handleCityBlur(e.target.value)}
            required
            placeholder="bv. Brussel"
            data-testid="property-city"
            autoComplete="off"
          />
          {/* City suggestions dropdown */}
          {showCitySuggestions && citySuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
              {citySuggestions.map((suggestion, index) => (
                <div
                  key={`property-${suggestion.postcode}-${suggestion.city}-${index}`}
                  className="px-3 py-2 hover:bg-muted cursor-pointer flex justify-between items-center"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleCitySuggestionSelect(suggestion);
                  }}
                >
                  <span>{suggestion.city}</span>
                  <span className="text-muted-foreground text-sm">{suggestion.postcode}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="country">Land</Label>
        <Input
          id="country"
          value={formData.country}
          onChange={(e) => handleChange('country', e.target.value)}
          data-testid="property-country"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notities</Label>
        <Textarea
          id="notes"
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={3}
          data-testid="property-notes"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="submit" disabled={loading || !formData.customer_id} data-testid="property-submit">
          {loading ? 'Opslaan...' : (property ? 'Bijwerken' : 'Aanmaken')}
        </Button>
      </DialogFooter>
    </form>
  );
};

// Installation Form Component
const InstallationForm = ({ installation, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState(installation || {
    installation_type: 'solar_panels',
    installation_date: '',
    panel_brand: '',
    panel_wattage: '',
    number_of_panels: '',
    inverter_brand: '',
    inverter_model: '',
    battery_brand: '',
    battery_capacity: '',
    charger_brand: '',
    charger_power: '',
    notes: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Clean up the data - remove empty strings and convert to proper types
    const cleanedData = {};
    for (const [key, value] of Object.entries(formData)) {
      if (value !== '' && value !== null && value !== undefined) {
        cleanedData[key] = value;
      }
    }
    onSubmit(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Type Installatie *</Label>
        <Select value={formData.installation_type} onValueChange={(v) => handleChange('installation_type', v)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="solar_panels">Zonnepanelen</SelectItem>
            <SelectItem value="battery">Batterij</SelectItem>
            <SelectItem value="ev_charger">Laadpaal</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Datum Plaatsing</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formData.installation_date ? format(parse(formData.installation_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={formData.installation_date ? parse(formData.installation_date, 'yyyy-MM-dd', new Date()) : undefined}
              onSelect={(date) => date && handleChange('installation_date', format(date, 'yyyy-MM-dd'))}
              locale={nl}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {formData.installation_type === 'solar_panels' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Merk Panelen</Label>
              <Input
                value={formData.panel_brand || ''}
                onChange={(e) => handleChange('panel_brand', e.target.value)}
                placeholder="bv. DMEGC, SunPower"
              />
            </div>
            <div className="space-y-2">
              <Label>Aantal Panelen</Label>
              <Input
                type="number"
                value={formData.number_of_panels || ''}
                onChange={(e) => handleChange('number_of_panels', parseInt(e.target.value) || '')}
                placeholder="bv. 10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Vermogen per Paneel (Wp)</Label>
            <Input
              type="number"
              value={formData.panel_wattage || ''}
              onChange={(e) => handleChange('panel_wattage', parseInt(e.target.value) || '')}
              placeholder="bv. 400"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Merk Omvormer</Label>
              <Input
                value={formData.inverter_brand || ''}
                onChange={(e) => handleChange('inverter_brand', e.target.value)}
                placeholder="bv. GoodWe, SMA"
              />
            </div>
            <div className="space-y-2">
              <Label>Model Omvormer</Label>
              <Input
                value={formData.inverter_model || ''}
                onChange={(e) => handleChange('inverter_model', e.target.value)}
                placeholder="bv. 3.6 kW"
              />
            </div>
          </div>
        </>
      )}

      {formData.installation_type === 'battery' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Merk Batterij</Label>
            <Input
              value={formData.battery_brand || ''}
              onChange={(e) => handleChange('battery_brand', e.target.value)}
              placeholder="bv. Tesla, LG"
            />
          </div>
          <div className="space-y-2">
            <Label>Capaciteit (kWh)</Label>
            <Input
              value={formData.battery_capacity || ''}
              onChange={(e) => handleChange('battery_capacity', e.target.value)}
              placeholder="bv. 13.5 kWh"
            />
          </div>
        </div>
      )}

      {formData.installation_type === 'ev_charger' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Merk Laadpaal</Label>
            <Input
              value={formData.charger_brand || ''}
              onChange={(e) => handleChange('charger_brand', e.target.value)}
              placeholder="bv. Wallbox, Alfen"
            />
          </div>
          <div className="space-y-2">
            <Label>Vermogen (kW)</Label>
            <Input
              value={formData.charger_power || ''}
              onChange={(e) => handleChange('charger_power', e.target.value)}
              placeholder="bv. 11 kW"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Notities</Label>
        <Textarea
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={2}
          placeholder="Extra opmerkingen..."
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Opslaan...' : (installation?.id ? 'Bijwerken' : 'Toevoegen')}
        </Button>
      </DialogFooter>
    </form>
  );
};

// Google Sheets gid values for installation quotes/invoices
const INSTALLATION_SHEET_GIDS = {
  offerte: {
    SAJ: '542498245',
    Solarwatt: '2141029867',
    DMEGC: '1654839085'
  },
  factuur: {
    SAJ: '2126887706',
    Solarwatt: '357251815',
    DMEGC: '1042547387'
  }
};

const GOOGLE_SHEET_BASE_URL = 'https://docs.google.com/spreadsheets/d/1Jp2j7rcPWuBbOTLCKtTaYlxvgJ-P0I-4Ww-NOxoLkW0/edit#gid=';

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [installations, setInstallations] = useState({});
  const [lastCleanings, setLastCleanings] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProperty, setEditProperty] = useState(null);
  const [editInstallation, setEditInstallation] = useState(null);
  const [installationDialogOpen, setInstallationDialogOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(50);
  // State for installation type selection modal
  const [installatieModalOpen, setInstallatieModalOpen] = useState(false);
  const [installatieModalType, setInstallatieModalType] = useState(null); // 'offerte' or 'factuur'
  const [installatieModalProperty, setInstallatieModalProperty] = useState(null);
  // State for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // { type: 'property' | 'installation', id: string, name: string }
  // State for Offerte Reiniging modal
  const [offerteReinigingModalOpen, setOfferteReinigingModalOpen] = useState(false);
  const [offerteReinigingProperty, setOfferteReinigingProperty] = useState(null);
  const [offerteReinigingForm, setOfferteReinigingForm] = useState({
    inplannen: false,
    inplanDatum: '',
    inplanTijd: '',
    bevestigingsmail: false
  });
  // State for Elektriciteit offerte modal
  const [elektriciteitModalOpen, setElektriciteitModalOpen] = useState(false);
  const [elektriciteitProperty, setElektriciteitProperty] = useState(null);
  const [elektriciteitFile, setElektriciteitFile] = useState(null);
  const [elektriciteitUploading, setElektriciteitUploading] = useState(false);
  const elektriciteitInputRef = useRef(null);
  const [searchParams] = useSearchParams();
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();
  
  // Filter Sheet state
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  
  // Combined filter state
  const defaultFilters = {
    cleaningContract: null, // null = all, '2_yearly', '3_yearly', 'none'
    status: null, // null = all, 'interesse', 'offerte', 'getekend', 'no_contact', 'contacted'
    hasSolarPanels: null,
    hasBattery: null,
    hasEVCharger: null,
    hasInverter: null,
    cities: [],
    postalCodes: [],
  };
  
  const [filters, setFilters] = useState(defaultFilters);
  
  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.cleaningContract !== null) count++;
    if (filters.status !== null) count++;
    if (filters.hasSolarPanels !== null) count++;
    if (filters.hasBattery !== null) count++;
    if (filters.hasEVCharger !== null) count++;
    if (filters.hasInverter !== null) count++;
    if (filters.cities.length > 0) count++;
    if (filters.postalCodes.length > 0) count++;
    return count;
  }, [filters]);
  
  // Reset filters
  const resetFilters = () => {
    setFilters(defaultFilters);
  };
  
  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const uniqueCities = [...new Set(properties.map(p => p.city).filter(Boolean))].sort();
    const uniquePostalCodes = [...new Set(properties.map(p => p.postal_code).filter(Boolean))].sort();
    return { cities: uniqueCities, postalCodes: uniquePostalCodes };
  }, [properties]);

  const preselectedCustomer = searchParams.get('customer');

  const fetchData = async () => {
    try {
      const [propsRes, custsRes, cleaningsRes, installationsRes] = await Promise.all([
        propertyAPI.getAll(),  // Get all properties
        customerAPI.getAll(),
        cleaningAPI.getAll(),
        installationAPI.getAll()
      ]);
      setProperties(propsRes.data);
      setCustomers(custsRes.data);

      // Build last cleaning map per property
      const cleaningMap = {};
      cleaningsRes.data.forEach(c => {
        if (!cleaningMap[c.property_id] || c.cleaning_date > cleaningMap[c.property_id].cleaning_date) {
          cleaningMap[c.property_id] = c;
        }
      });
      setLastCleanings(cleaningMap);

      // Build installations map per property from single API call
      const instMap = {};
      installationsRes.data.forEach(inst => {
        if (!instMap[inst.property_id]) {
          instMap[inst.property_id] = [];
        }
        instMap[inst.property_id].push(inst);
      });
      setInstallations(instMap);
    } catch (error) {
      toast.error('Kon adressen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Client-side filtering on street, city, postal code, and all filters
  const filteredProperties = useMemo(() => {
    return properties.filter(p => {
      // Text search filter
      const matchesSearch = !search || (
        p.street?.toLowerCase().includes(search.toLowerCase()) ||
        p.city?.toLowerCase().includes(search.toLowerCase()) ||
        p.postal_code?.toLowerCase().includes(search.toLowerCase()) ||
        p.number?.toLowerCase().includes(search.toLowerCase())
      );
      
      // Cleaning contract filter
      let matchesContract = true;
      if (filters.cleaningContract !== null) {
        if (filters.cleaningContract === 'none') {
          matchesContract = !p.cleaning_contract;
        } else {
          matchesContract = p.cleaning_contract === filters.cleaningContract;
        }
      }
      
      // Status filter
      let matchesStatus = true;
      if (filters.status !== null) {
        switch (filters.status) {
          case 'interesse':
            matchesStatus = p.interesse_reiniging === true;
            break;
          case 'offerte':
            matchesStatus = p.offerte_ontvangen === true;
            break;
          case 'getekend':
            matchesStatus = p.offerte_getekend === true;
            break;
          case 'no_contact':
            matchesStatus = !p.call_1 && !p.call_2 && !p.call_3 && !p.mail_1 && !p.mail_2 && !p.mail_3;
            break;
          case 'contacted':
            matchesStatus = p.call_1 || p.call_2 || p.call_3 || p.mail_1 || p.mail_2 || p.mail_3;
            break;
          default:
            matchesStatus = true;
        }
      }
      
      // Installation filters
      const propertyInstallations = installations[p.id] || [];
      
      let matchesInstallations = true;
      if (filters.hasSolarPanels !== null) {
        const has = propertyInstallations.some(i => i.installation_type === 'solar_panels');
        matchesInstallations = matchesInstallations && (filters.hasSolarPanels ? has : !has);
      }
      if (filters.hasBattery !== null) {
        const has = propertyInstallations.some(i => i.installation_type === 'battery');
        matchesInstallations = matchesInstallations && (filters.hasBattery ? has : !has);
      }
      if (filters.hasEVCharger !== null) {
        const has = propertyInstallations.some(i => i.installation_type === 'ev_charger');
        matchesInstallations = matchesInstallations && (filters.hasEVCharger ? has : !has);
      }
      if (filters.hasInverter !== null) {
        const has = propertyInstallations.some(i => i.installation_type === 'inverter');
        matchesInstallations = matchesInstallations && (filters.hasInverter ? has : !has);
      }
      
      // City filter
      let matchesCity = true;
      if (filters.cities.length > 0) {
        matchesCity = filters.cities.includes(p.city);
      }
      
      // Postal code filter
      let matchesPostal = true;
      if (filters.postalCodes.length > 0) {
        matchesPostal = filters.postalCodes.includes(p.postal_code);
      }
      
      return matchesSearch && matchesContract && matchesStatus && matchesInstallations && matchesCity && matchesPostal;
    });
  }, [properties, search, filters, installations]);

  useEffect(() => {
    if (preselectedCustomer && customers.length > 0) {
      setDialogOpen(true);
    }
  }, [preselectedCustomer, customers]);

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      await propertyAPI.create(data);
      toast.success('Property created successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    setSaving(true);
    try {
      await propertyAPI.update(editProperty.id, data);
      toast.success('Property updated successfully');
      setEditProperty(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update property');
    } finally {
      setSaving(false);
    }
  };

  const handleSetCleaningContract = async (propertyId, contractType) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}/cleaning-contract?contract_type=${contractType || ''}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const label = contractType === '2_yearly' ? '2-jaarlijks' : contractType === '3_yearly' ? '3-jaarlijks' : 'verwijderd';
      toast.success(`Reinigingscontract ${label}`);
      fetchData();
    } catch (error) {
      toast.error('Kon contract niet bijwerken');
    }
  };

  const handleDelete = async (id) => {
    const property = properties.find(p => p.id === id);
    const propertyName = property ? `${property.street} ${property.number}` : 'dit adres';
    setItemToDelete({ type: 'property', id, name: propertyName });
    setDeleteConfirmOpen(true);
  };

  // Installation handlers
  const handleAddInstallation = (propertyId) => {
    setSelectedPropertyId(propertyId);
    setEditInstallation(null);
    setInstallationDialogOpen(true);
  };

  const handleEditInstallation = (installation, propertyId) => {
    setSelectedPropertyId(propertyId);
    setEditInstallation(installation);
    setInstallationDialogOpen(true);
  };

  const handleSaveInstallation = async (data) => {
    setSaving(true);
    try {
      if (editInstallation?.id) {
        await installationAPI.update(editInstallation.id, { ...data, property_id: selectedPropertyId });
        toast.success('Installatie bijgewerkt');
      } else {
        await installationAPI.create({ ...data, property_id: selectedPropertyId });
        toast.success('Installatie toegevoegd');
      }
      setInstallationDialogOpen(false);
      setEditInstallation(null);
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : 'Kon installatie niet opslaan');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInstallation = async (installationId) => {
    setItemToDelete({ type: 'installation', id: installationId, name: 'deze installatie' });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (itemToDelete.type === 'property') {
        await propertyAPI.delete(itemToDelete.id);
        toast.success('Adres verwijderd');
      } else if (itemToDelete.type === 'installation') {
        await installationAPI.delete(itemToDelete.id);
        toast.success('Installatie verwijderd');
      }
      fetchData();
    } catch (error) {
      const errorMsg = itemToDelete.type === 'property' 
        ? 'Kon adres niet verwijderen' 
        : 'Kon installatie niet verwijderen';
      toast.error(error.response?.data?.detail || errorMsg);
    } finally {
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  // Open Offerte Reiniging modal
  const handleOfferteReinigingClick = (property) => {
    setOfferteReinigingProperty(property);
    setOfferteReinigingForm({
      inplannen: false,
      inplanDatum: '',
      inplanTijd: '',
      bevestigingsmail: false
    });
    setOfferteReinigingModalOpen(true);
  };

  // Submit Offerte Reiniging with options
  const handleOfferteReinigingSubmit = async () => {
    const property = offerteReinigingProperty;
    setOfferteReinigingModalOpen(false);
    
    try {
      toast.loading('Offerte aanvragen...', { id: 'offerte' });
      
      // Get customer name and email for this property
      const customer = customers.find(c => c.id === property.customer_id);
      const customerName = customer ? `${customer.first_name} ${customer.last_name}` : 'Onbekend';
      
      // Get logged-in user info
      const medewerkerNaam = user ? `${user.first_name} ${user.last_name}` : 'Onbekend';
      const medewerkerEmail = user?.email || '';
      
      // Build the message for the AI agent
      let aiMessage = `MEDEWERKER: ${medewerkerNaam} (${medewerkerEmail}) heeft deze offerte aangevraagd. `;
      
      if (offerteReinigingForm.inplannen && offerteReinigingForm.inplanDatum) {
        const datum = offerteReinigingForm.inplanDatum;
        const tijd = offerteReinigingForm.inplanTijd || '09:00';
        aiMessage += `ACTIE 1: Plan deze reiniging in de Google Calendar op ${datum} om ${tijd} uur voor klant ${customerName} op adres ${property.street} ${property.number}, ${property.city}. `;
      }
      if (offerteReinigingForm.bevestigingsmail) {
        aiMessage += `ACTIE 2: Schrijf een bevestigingsmail GERICHT AAN DE KLANT (aanhef: "Beste ${customerName}"). De mail is voor de klant, NIET voor de medewerker. Verzend deze mail naar ${medewerkerEmail} (${medewerkerNaam}). De medewerker controleert de mail en stuurt deze zelf door naar de klant.`;
      }
      
      // Call backend endpoint with extra data
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/offerte-reiniging-adres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          property_id: property.id,
          inplannen: offerteReinigingForm.inplannen,
          inplan_datum: offerteReinigingForm.inplanDatum,
          inplan_tijd: offerteReinigingForm.inplanTijd || '09:00',
          bevestigingsmail: offerteReinigingForm.bevestigingsmail,
          gebruiker_email: user?.email,
          gebruiker_naam: user ? `${user.first_name} ${user.last_name}` : '',
          ai_instructie: aiMessage
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Offerte aanvraag verzonden!', { id: 'offerte' });
        window.open('https://docs.google.com/spreadsheets/d/1Jp2j7rcPWuBbOTLCKtTaYlxvgJ-P0I-4Ww-NOxoLkW0/edit#gid=78034772', '_blank');
      } else if (data.message && data.message.includes('404')) {
        toast.error('n8n workflow niet actief', { id: 'offerte' });
      } else {
        throw new Error(data.message || 'Webhook error');
      }
    } catch (error) {
      console.error('Offerte error:', error);
      toast.error('Kon offerte aanvraag niet verzenden', { id: 'offerte' });
    }
  };

  const handleFactuurReiniging = async (property) => {
    try {
      toast.loading('Factuur aanvragen...', { id: 'factuur' });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/factuur-reiniging-adres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ property_id: property.id })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Factuur aanvraag verzonden!', { id: 'factuur' });
        window.open('https://docs.google.com/spreadsheets/d/1Jp2j7rcPWuBbOTLCKtTaYlxvgJ-P0I-4Ww-NOxoLkW0/edit#gid=1030317467', '_blank');
      } else if (data.message && data.message.includes('404')) {
        toast.error('n8n workflow niet actief', { id: 'factuur' });
      } else {
        throw new Error(data.message || 'Webhook error');
      }
    } catch (error) {
      console.error('Factuur error:', error);
      toast.error('Kon factuur aanvraag niet verzenden', { id: 'factuur' });
    }
  };

  // Open modal to select installation type (SAJ, Solarwatt, DMEGC)
  const handleOfferteInstallatieClick = (property) => {
    setInstallatieModalProperty(property);
    setInstallatieModalType('offerte');
    setInstallatieModalOpen(true);
  };

  const handleFactuurInstallatieClick = (property) => {
    setInstallatieModalProperty(property);
    setInstallatieModalType('factuur');
    setInstallatieModalOpen(true);
  };

  // Handle the actual webhook call and redirect after selecting type
  const handleInstallatieTypeSelect = async (installatieType) => {
    const property = installatieModalProperty;
    const type = installatieModalType;
    setInstallatieModalOpen(false);
    
    const toastId = type === 'offerte' ? 'offerte-inst' : 'factuur-inst';
    const toastLabel = type === 'offerte' ? 'Offerte' : 'Factuur';
    const endpoint = type === 'offerte' ? 'offerte-installatie-adres' : 'factuur-installatie-adres';
    
    try {
      toast.loading(`${toastLabel} installatie aanvragen...`, { id: toastId });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ property_id: property.id })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`${toastLabel} installatie aanvraag verzonden!`, { id: toastId });
        // Open Google Sheet with the correct gid based on selected type
        const gid = INSTALLATION_SHEET_GIDS[type][installatieType];
        window.open(`${GOOGLE_SHEET_BASE_URL}${gid}`, '_blank');
      } else {
        throw new Error(data.message || 'Webhook error');
      }
    } catch (error) {
      console.error(`${toastLabel} installatie error:`, error);
      toast.error(`Kon ${toastLabel.toLowerCase()} aanvraag niet verzenden`, { id: toastId });
    }
  };

  // Handle Elektriciteit offerte
  const handleOfferteElektriciteitClick = (property) => {
    setElektriciteitProperty(property);
    setElektriciteitFile(null);
    setElektriciteitModalOpen(true);
  };

  const handleElektriciteitFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Alleen PDF bestanden zijn toegestaan');
        return;
      }
      setElektriciteitFile(file);
    }
  };

  const handleElektriciteitSubmit = async () => {
    if (!elektriciteitFile || !elektriciteitProperty) {
      toast.error('Selecteer eerst een PDF bestand');
      return;
    }

    setElektriciteitUploading(true);
    try {
      const response = await offerteAPI.createElektriciteit(elektriciteitProperty.id, elektriciteitFile);
      
      if (response.data.success) {
        toast.success('PDF geüpload en klantgegevens verzonden!');
        setElektriciteitModalOpen(false);
        setElektriciteitFile(null);
        setElektriciteitProperty(null);
        
        // Open the Google Sheet for Elektriciteit offertes
        window.open('https://docs.google.com/spreadsheets/d/1Jp2j7rcPWuBbOTLCKtTaYlxvgJ-P0I-4Ww-NOxoLkW0/edit?gid=609459326#gid=609459326', '_blank');
      }
    } catch (error) {
      console.error('Elektriciteit offerte error:', error);
      toast.error(error.response?.data?.detail || 'Kon offerte niet verwerken');
    } finally {
      setElektriciteitUploading(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.first_name} ${customer.last_name}` : '';
  };

  const getCustomer = (customerId) => {
    return customers.find(c => c.id === customerId);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="properties-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Adressen</h1>
          <p className="text-muted-foreground mt-1">Beheer adressen en installaties</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-property-btn">
              <Plus className="w-4 h-4 mr-2" />
              Adres Toevoegen
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nieuw Adres Toevoegen</DialogTitle>
              <DialogDescription>Vul de adresgegevens in</DialogDescription>
            </DialogHeader>
            <PropertyForm
              property={preselectedCustomer ? { customer_id: preselectedCustomer } : null}
              customers={customers}
              onSubmit={handleCreate}
              onCancel={() => setDialogOpen(false)}
              loading={saving}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Zoeken op straat, stad, postcode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="property-search"
          />
        </div>
        
        <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="filter-btn">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center justify-between">
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="text-muted-foreground">
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                )}
              </SheetTitle>
              <SheetDescription>
                Filter adressen op verschillende criteria
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-2">
              {/* Reinigingscontract Filter */}
              <FilterSection title="Reinigingscontract" icon={Sparkles} defaultOpen={true}>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant={filters.cleaningContract === '2_yearly' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, cleaningContract: prev.cleaningContract === '2_yearly' ? null : '2_yearly' }))}
                    className="w-full"
                  >
                    <Sparkles className="w-3 h-3 mr-1 text-amber-500" />
                    2-jaarlijks
                  </Button>
                  <Button 
                    variant={filters.cleaningContract === '3_yearly' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, cleaningContract: prev.cleaningContract === '3_yearly' ? null : '3_yearly' }))}
                    className="w-full"
                  >
                    <Sparkles className="w-3 h-3 mr-1 text-blue-500" />
                    3-jaarlijks
                  </Button>
                  <Button 
                    variant={filters.cleaningContract === 'none' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, cleaningContract: prev.cleaningContract === 'none' ? null : 'none' }))}
                    className="w-full col-span-2"
                  >
                    Geen contract
                  </Button>
                </div>
              </FilterSection>

              {/* Status Filter */}
              <FilterSection title="Status" icon={Phone} defaultOpen={true}>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant={filters.status === 'no_contact' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'no_contact' ? null : 'no_contact' }))}
                    className="w-full"
                  >
                    Geen contact
                  </Button>
                  <Button 
                    variant={filters.status === 'contacted' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'contacted' ? null : 'contacted' }))}
                    className="w-full"
                  >
                    Gecontacteerd
                  </Button>
                  <Button 
                    variant={filters.status === 'interesse' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'interesse' ? null : 'interesse' }))}
                    className="w-full text-amber-500 border-amber-500/30"
                  >
                    Interesse
                  </Button>
                  <Button 
                    variant={filters.status === 'offerte' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'offerte' ? null : 'offerte' }))}
                    className="w-full text-blue-500 border-blue-500/30"
                  >
                    Offerte
                  </Button>
                  <Button 
                    variant={filters.status === 'getekend' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'getekend' ? null : 'getekend' }))}
                    className="w-full col-span-2 text-green-500 border-green-500/30"
                  >
                    Getekend
                  </Button>
                </div>
              </FilterSection>

              {/* Installaties Filter */}
              <FilterSection title="Installaties" icon={Sun} defaultOpen={false}>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Zonnepanelen</Label>
                    <div className="flex gap-2">
                      <Button 
                        variant={filters.hasSolarPanels === true ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasSolarPanels: prev.hasSolarPanels === true ? null : true }))}
                        className="flex-1"
                      >
                        <Sun className="w-3 h-3 mr-1" />
                        Heeft
                      </Button>
                      <Button 
                        variant={filters.hasSolarPanels === false ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasSolarPanels: prev.hasSolarPanels === false ? null : false }))}
                        className="flex-1"
                      >
                        Geen
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Batterij</Label>
                    <div className="flex gap-2">
                      <Button 
                        variant={filters.hasBattery === true ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasBattery: prev.hasBattery === true ? null : true }))}
                        className="flex-1"
                      >
                        <Battery className="w-3 h-3 mr-1" />
                        Heeft
                      </Button>
                      <Button 
                        variant={filters.hasBattery === false ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasBattery: prev.hasBattery === false ? null : false }))}
                        className="flex-1"
                      >
                        Geen
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Laadpaal</Label>
                    <div className="flex gap-2">
                      <Button 
                        variant={filters.hasEVCharger === true ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasEVCharger: prev.hasEVCharger === true ? null : true }))}
                        className="flex-1"
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        Heeft
                      </Button>
                      <Button 
                        variant={filters.hasEVCharger === false ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasEVCharger: prev.hasEVCharger === false ? null : false }))}
                        className="flex-1"
                      >
                        Geen
                      </Button>
                    </div>
                  </div>
                </div>
              </FilterSection>

              {/* Gemeente Filter */}
              <FilterSection title="Gemeente" icon={MapPin} defaultOpen={false}>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {filterOptions.cities.map(city => (
                    <div key={city} className="flex items-center space-x-2">
                      <Checkbox
                        id={`city-${city}`}
                        checked={filters.cities.includes(city)}
                        onCheckedChange={(checked) => {
                          setFilters(prev => ({
                            ...prev,
                            cities: checked 
                              ? [...prev.cities, city]
                              : prev.cities.filter(c => c !== city)
                          }));
                        }}
                      />
                      <label htmlFor={`city-${city}`} className="text-sm cursor-pointer">
                        {city}
                      </label>
                    </div>
                  ))}
                </div>
              </FilterSection>
            </div>

            <SheetFooter className="mt-6">
              <Button onClick={() => setFilterSheetOpen(false)} className="w-full">
                Filters toepassen
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
        
        {(search || activeFilterCount > 0) && (
          <span className="text-sm text-muted-foreground">
            {filteredProperties.length} van {properties.length} adressen
          </span>
        )}
      </div>

      {/* Property List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="h-48 animate-pulse bg-muted" />
          ))}
        </div>
      ) : filteredProperties.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Geen adressen gevonden</p>
            <p className="text-sm mt-1">{search ? 'Probeer een andere zoekterm' : 'Voeg je eerste adres toe'}</p>
          </div>
        </Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProperties.slice(0, displayLimit).map((property) => (
            <Card 
              key={property.id} 
              className="property-card"
              onClick={() => navigate(`/properties/${property.id}`)}
              data-testid={`property-card-${property.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-blue-500/20 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium">{property.street} {property.number}</p>
                      <p className="text-sm text-muted-foreground">
                        {property.postal_code} {property.city}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/properties/${property.id}`); }}>
                        <Eye className="w-4 h-4 mr-2" />
                        Bekijken
                      </DropdownMenuItem>
                      {hasRole(['admin', 'sales']) && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditProperty(property); }}>
                          <Edit className="w-4 h-4 mr-2" />
                          Bewerken
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground">Reinigingscontract</DropdownMenuLabel>
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); handleSetCleaningContract(property.id, '2_yearly'); }}
                        className={property.cleaning_contract === '2_yearly' ? 'bg-primary/10' : ''}
                      >
                        <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                        2-jaarlijks {property.cleaning_contract === '2_yearly' && '✓'}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); handleSetCleaningContract(property.id, '3_yearly'); }}
                        className={property.cleaning_contract === '3_yearly' ? 'bg-primary/10' : ''}
                      >
                        <Sparkles className="w-4 h-4 mr-2 text-blue-500" />
                        3-jaarlijks {property.cleaning_contract === '3_yearly' && '✓'}
                      </DropdownMenuItem>
                      {property.cleaning_contract && (
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); handleSetCleaningContract(property.id, null); }}
                          className="text-muted-foreground"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Contract verwijderen
                        </DropdownMenuItem>
                      )}
                      {hasRole('admin') && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDelete(property.id); }}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Verwijderen
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Cleaning Contract Badge */}
                {property.cleaning_contract && (
                  <div className="mb-2">
                    <Badge 
                      variant="outline" 
                      className={property.cleaning_contract === '2_yearly' ? 'border-amber-500 text-amber-500' : 'border-blue-500 text-blue-500'}
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      {property.cleaning_contract === '2_yearly' ? '2-jaarlijks' : '3-jaarlijks'} contract
                    </Badge>
                  </div>
                )}

                {/* Customer */}
                {getCustomer(property.customer_id) && (
                  <Link 
                    to={`/customers/${property.customer_id}`}
                    className="text-xs text-primary hover:underline mb-3 block"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Eigenaar: {getCustomerName(property.customer_id)}
                  </Link>
                )}

                {/* Installations */}
                <div className="space-y-2">
                  {installations[property.id]?.length > 0 ? (
                    installations[property.id].map((inst) => (
                      <div key={inst.id} className="text-xs border-t border-border/50 pt-2 first:border-0 first:pt-0 group">
                        <div className="flex items-center justify-between mb-1">
                          <Badge 
                            variant="outline" 
                            className={getInstallationTypeBadgeClass(inst.installation_type)}
                          >
                            {inst.installation_type === 'solar_panels' && <Sun className="w-3 h-3 mr-1" />}
                            {inst.installation_type === 'battery' && <Battery className="w-3 h-3 mr-1" />}
                            {inst.installation_type === 'ev_charger' && <Zap className="w-3 h-3 mr-1" />}
                            {getInstallationTypeLabel(inst.installation_type)}
                          </Badge>
                          {hasRole(['admin', 'technician']) && (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6"
                                onClick={(e) => { e.stopPropagation(); handleEditInstallation(inst, property.id); }}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 text-destructive hover:text-destructive"
                                onClick={(e) => { e.stopPropagation(); handleDeleteInstallation(inst.id); }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="text-muted-foreground space-y-0.5 pl-1">
                          {inst.installation_type === 'solar_panels' && (
                            <>
                              {inst.panel_brand && <p>Merk panelen: <span className="text-foreground">{inst.panel_brand}</span></p>}
                              {inst.number_of_panels && <p>Aantal panelen: <span className="text-foreground">{inst.number_of_panels}</span></p>}
                              {inst.panel_wattage && <p>Vermogen: <span className="text-foreground">{inst.panel_wattage} Wp</span></p>}
                              {inst.number_of_panels && inst.panel_wattage && (
                                <p>Totaal: <span className="text-foreground font-medium">{(inst.number_of_panels * inst.panel_wattage / 1000).toFixed(2)} kWp</span></p>
                              )}
                              {(inst.inverter_brand || inst.inverter_model) && (
                                <p>Omvormer: <span className="text-foreground">{[inst.inverter_brand, inst.inverter_model].filter(Boolean).join(' ')}</span></p>
                              )}
                            </>
                          )}
                          {inst.installation_type === 'battery' && (
                            <>
                              {inst.battery_brand && <p>Merk: <span className="text-foreground">{inst.battery_brand}</span></p>}
                              {inst.battery_capacity && <p>Capaciteit: <span className="text-foreground">{inst.battery_capacity}{!String(inst.battery_capacity).toLowerCase().includes('kwh') ? ' kWh' : ''}</span></p>}
                            </>
                          )}
                          {inst.installation_type === 'ev_charger' && (
                            <>
                              {inst.charger_brand && <p>Merk: <span className="text-foreground">{inst.charger_brand}</span></p>}
                              {inst.charger_power && <p>Vermogen: <span className="text-foreground">{inst.charger_power}</span></p>}
                            </>
                          )}
                          {inst.installation_date && (
                            <p className="flex items-center gap-1">
                              <CalendarIcon className="w-3 h-3" />
                              Geplaatst: <span className="text-foreground">{formatDate(inst.installation_date)}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <span className="text-xs text-muted-foreground">Geen installaties</span>
                  )}

                  {/* Add Installation Button */}
                  {hasRole(['admin', 'technician']) && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-2 text-xs h-7"
                      onClick={(e) => { e.stopPropagation(); handleAddInstallation(property.id); }}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Installatie Toevoegen
                    </Button>
                  )}

                  {/* Laatste Reiniging */}
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <div className="flex items-center gap-2 text-xs">
                      <Sparkles className="w-3 h-3 text-purple-400" />
                      {lastCleanings[property.id] ? (
                        <span className={needsCleaning(lastCleanings[property.id].cleaning_date) ? 'text-amber-400' : 'text-muted-foreground'}>
                          Laatste reiniging: <span className="text-foreground">{formatDate(lastCleanings[property.id].cleaning_date)}</span>
                          {needsCleaning(lastCleanings[property.id].cleaning_date) && (
                            <span className="ml-2 text-amber-400 flex items-center gap-1 inline-flex">
                              <AlertTriangle className="w-3 h-3" /> Reiniging nodig
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-amber-400 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Nog nooit gereinigd
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-3 pt-3 border-t border-border/50 flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full text-xs h-8"
                      onClick={(e) => { e.stopPropagation(); window.open(getGoogleMapsUrl(property), '_blank'); }}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Google Maps
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Load More Button */}
        {filteredProperties.length > displayLimit && (
          <div className="flex justify-center mt-6">
            <Button 
              variant="outline" 
              onClick={() => setDisplayLimit(prev => prev + 50)}
            >
              Meer laden ({filteredProperties.length - displayLimit} resterend)
            </Button>
          </div>
        )}
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editProperty} onOpenChange={() => setEditProperty(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adres Bewerken</DialogTitle>
            <DialogDescription>Pas de adresgegevens aan</DialogDescription>
          </DialogHeader>
          {editProperty && (
            <PropertyForm
              property={editProperty}
              customers={customers}
              onSubmit={handleUpdate}
              onCancel={() => setEditProperty(null)}
              loading={saving}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Installation Dialog */}
      <Dialog open={installationDialogOpen} onOpenChange={setInstallationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editInstallation ? 'Installatie Bewerken' : 'Installatie Toevoegen'}</DialogTitle>
            <DialogDescription>
              {editInstallation ? 'Pas de installatiegegevens aan' : 'Voeg een nieuwe installatie toe aan dit adres'}
            </DialogDescription>
          </DialogHeader>
          <InstallationForm
            installation={editInstallation}
            onSubmit={handleSaveInstallation}
            onCancel={() => { setInstallationDialogOpen(false); setEditInstallation(null); }}
            loading={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Offerte Reiniging Modal */}
      <Dialog open={offerteReinigingModalOpen} onOpenChange={setOfferteReinigingModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Offerte reiniging</DialogTitle>
            <DialogDescription>
              {offerteReinigingProperty && (
                <>Offerte voor {offerteReinigingProperty.street} {offerteReinigingProperty.number}, {offerteReinigingProperty.city}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Inplannen optie */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="inplannen"
                  checked={offerteReinigingForm.inplannen}
                  onCheckedChange={(checked) => setOfferteReinigingForm(prev => ({ ...prev, inplannen: checked }))}
                />
                <Label htmlFor="inplannen" className="font-medium cursor-pointer">
                  <CalendarIcon className="w-4 h-4 inline mr-2" />
                  Inplannen in Google Calendar
                </Label>
              </div>
              {offerteReinigingForm.inplannen && (
                <div className="ml-7 grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="inplanDatum">Datum</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {offerteReinigingForm.inplanDatum ? format(parse(offerteReinigingForm.inplanDatum, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={offerteReinigingForm.inplanDatum ? parse(offerteReinigingForm.inplanDatum, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => date && setOfferteReinigingForm(prev => ({ ...prev, inplanDatum: format(date, 'yyyy-MM-dd') }))}
                          locale={nl}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inplanTijd">Tijd</Label>
                    <Input
                      id="inplanTijd"
                      type="time"
                      value={offerteReinigingForm.inplanTijd}
                      onChange={(e) => setOfferteReinigingForm(prev => ({ ...prev, inplanTijd: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Bevestigingsmail optie */}
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="bevestigingsmail"
                  checked={offerteReinigingForm.bevestigingsmail}
                  onCheckedChange={(checked) => setOfferteReinigingForm(prev => ({ ...prev, bevestigingsmail: checked }))}
                />
                <Label htmlFor="bevestigingsmail" className="font-medium cursor-pointer">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Bevestigingsmail sturen naar {offerteReinigingProperty && getCustomerName(offerteReinigingProperty.customer_id)}
                </Label>
              </div>
              {offerteReinigingForm.bevestigingsmail && user?.email && (
                <p className="ml-7 text-sm text-muted-foreground">
                  De mail wordt verstuurd naar <span className="text-primary">{user.email}</span> ter controle. 
                  Je stuurt deze zelf door naar de klant incl. offerte en Algemene Voorwaarden.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferteReinigingModalOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleOfferteReinigingSubmit}>
              <FileText className="w-4 h-4 mr-2" />
              Offerte verzenden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Installatie Type Selection Modal */}
      <Dialog open={installatieModalOpen} onOpenChange={setInstallatieModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {installatieModalType === 'offerte' ? 'Offerte installatie' : 'Factuur installatie'}
            </DialogTitle>
            <DialogDescription>
              Selecteer het type installatie
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button 
              variant="outline" 
              className="w-full h-12 text-base justify-start px-4 hover:bg-blue-500/10 hover:border-blue-500/50"
              onClick={() => handleInstallatieTypeSelect('SAJ')}
              data-testid="installatie-type-saj"
            >
              <Sun className="w-5 h-5 mr-3 text-blue-400" />
              SAJ
            </Button>
            <Button 
              variant="outline" 
              className="w-full h-12 text-base justify-start px-4 hover:bg-amber-500/10 hover:border-amber-500/50"
              onClick={() => handleInstallatieTypeSelect('Solarwatt')}
              data-testid="installatie-type-solarwatt"
            >
              <Sun className="w-5 h-5 mr-3 text-amber-400" />
              Solarwatt
            </Button>
            <Button 
              variant="outline" 
              className="w-full h-12 text-base justify-start px-4 hover:bg-green-500/10 hover:border-green-500/50"
              onClick={() => handleInstallatieTypeSelect('DMEGC')}
              data-testid="installatie-type-dmegc"
            >
              <Sun className="w-5 h-5 mr-3 text-green-400" />
              DMEGC
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Weet je het zeker?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je {itemToDelete?.name} wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Offerte Elektriciteit Dialog */}
      <Dialog open={elektriciteitModalOpen} onOpenChange={setElektriciteitModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Offerte Elektriciteit</DialogTitle>
            <DialogDescription>
              Upload een PDF bestand. Dit wordt automatisch naar Google Drive geüpload en de klantgegevens worden naar n8n verzonden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Property & Customer Info */}
            {elektriciteitProperty && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="font-medium">{getCustomerName(elektriciteitProperty.customer_id)}</p>
                <p className="text-muted-foreground">
                  {elektriciteitProperty.street} {elektriciteitProperty.number}, {elektriciteitProperty.city}
                </p>
              </div>
            )}

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="elektriciteit-pdf-props">PDF Bestand *</Label>
              <div className="flex flex-col gap-2">
                <Input
                  id="elektriciteit-pdf-props"
                  ref={elektriciteitInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleElektriciteitFileChange}
                  className="cursor-pointer"
                  data-testid="elektriciteit-file-input-props"
                />
                {elektriciteitFile && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground bg-green-500/10 border border-green-500/30 rounded-md p-2">
                    <FileText className="w-4 h-4 text-green-500" />
                    <span className="truncate flex-1">{elektriciteitFile.name}</span>
                    <span>({(elektriciteitFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Info text */}
            <div className="text-xs text-muted-foreground bg-blue-500/10 border border-blue-500/30 rounded-md p-3">
              <p className="font-medium text-blue-400 mb-1">Wat gebeurt er?</p>
              <ul className="list-disc list-inside space-y-1">
                <li>PDF wordt geüpload naar Google Drive (Elektriciteit map)</li>
                <li>Klantgegevens worden automatisch naar n8n verzonden</li>
                <li>n8n detecteert de nieuwe PDF in Drive en verwerkt deze</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setElektriciteitModalOpen(false);
                setElektriciteitFile(null);
                setElektriciteitProperty(null);
              }}
              disabled={elektriciteitUploading}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleElektriciteitSubmit}
              disabled={!elektriciteitFile || elektriciteitUploading}
              data-testid="elektriciteit-submit-btn-props"
            >
              {elektriciteitUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploaden...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Verzenden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Properties;
